import { NavigationFlowHandler } from '@adapters/navigation/navigationFlowHandler';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import type { Logger } from '@core/model/domain/logger';
import { LoadingComponent } from '@shared/components/loading/loading.component';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { firstValueFrom } from 'rxjs';
import { loggerToken } from 'src/conf/di/angularProviderMapper';
import { InitVM } from './init.viewmodel';

@Component({
  selector: 'app-init',
  templateUrl: './init.component.html',
  standalone: true,
  imports: [LoadingComponent],
})
export class InitComponent implements OnInit {
  private initVM: InitVM;

  constructor(
    private activatedRoute: ActivatedRoute,
    private storeService: StoreService,
    private router: Router,
    private readonly processEventService: ProcessEventService,
    @Inject(loggerToken) private logger: Logger,
    private navigationFlowHandler: NavigationFlowHandler
  ) {
    this.initVM = new InitVM(
      this.storeService,
      this.router,
      this.logger,
      this.processEventService,
      this.navigationFlowHandler
    );
  }

  async ngOnInit(): Promise<void> {
    const {
      userToken,
      tagging,
      externalConnectionId,
      error,
      errorDescription,
      isWebauthRedirectionGoingToPartner,
    } = await this.getQueryParams();
    const { userId } = await this.getRouteParams();

    this.initVM.initUserJourney({
      userToken,
      tagging,
      userId,
      externalConnectionId,
      error,
      errorDescription,
      isWebauthRedirectionGoingToPartner,
    });
  }

  private async getRouteParams(): Promise<{ userId: string }> {
    const { userId } = await firstValueFrom(this.activatedRoute.params);

    if (!userId) {
      throw new Error('Route param userId is missing');
    }

    return { userId };
  }

  private async getQueryParams(): Promise<{
    userToken: string;
    tagging: string | null;
    externalConnectionId: string | null;
    error: string | null;
    errorDescription: string | null;
    isWebauthRedirectionGoingToPartner: string;
  }> {
    const {
      tagging,
      'user-token': userToken,
      id_connection: externalConnectionId,
      error,
      error_description: errorDescription,
      redirectPostWebauthToPartner: isWebauthRedirectionGoingToPartner,
    } = await firstValueFrom(this.activatedRoute.queryParams);

    if (!userToken) {
      throw new Error('Query param user-token is missing');
    }

    return {
      userToken,
      tagging,
      externalConnectionId,
      error,
      errorDescription,
      isWebauthRedirectionGoingToPartner,
    };
  }
}
